import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchMatchResults, exportMatchResultsCsv } from '../../api/matches';
import {
  fetchTender,
  fetchTenderSkills,
  updateTenderSkills,
  reextractTenderMetadata,
  embedTender,
  triggerMatching,
} from '../../api/tenders';
import { fetchTenderPersonas } from '../../api/analytics';
import type { Tender, MatchResult, TenderMatchSummary, TenderPersonaResult, WeightedSkill } from '../../types';
import MatchCard from '../../components/MatchCard';
import SkillBadge from '../../components/SkillBadge';
import SkillEditor from '../../components/SkillEditor';
import PersonaCard from '../../components/PersonaCard';

type ViewMode = 'cards' | 'table';

export default function MatchResults() {
  const { t } = useTranslation();
  const { tenderId } = useParams<{ tenderId: string }>();

  const [tender, setTender] = useState<Tender | null>(null);
  const [summary, setSummary] = useState<TenderMatchSummary | null>(null);
  const [results, setResults] = useState<MatchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [minScore, setMinScore] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<MatchResult | null>(null);
  const [personas, setPersonas] = useState<TenderPersonaResult | null>(null);
  const [weightedSkills, setWeightedSkills] = useState<WeightedSkill[]>([]);
  const [reextractStep, setReextractStep] = useState<string | null>(null);
  const [rerunStep, setRerunStep] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenderId) return;
    setLoading(true);
    setError(null);
    try {
      const [tenderData, resultsData, personaData, skillsData] = await Promise.all([
        fetchTender(tenderId),
        fetchMatchResults(tenderId, { sort_by: 'score', order: 'desc', page_size: 100 }),
        fetchTenderPersonas(tenderId).catch(() => null),
        fetchTenderSkills(tenderId).catch(() => [] as WeightedSkill[]),
      ]);
      setTender(tenderData);
      setPersonas(personaData);
      setWeightedSkills(skillsData);
      const items = resultsData.items;
      const scores = items.map((r) => r.score);
      setSummary({
        tender_id: tenderId,
        tender_title: tenderData.title,
        total_candidates: items.length,
        top_score: scores.length > 0 ? Math.max(...scores) : 0,
        average_score: scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0,
        results: items,
        generated_at: new Date().toISOString(),
      });
      setResults(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('matchResults.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [tenderId, t]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleExportCsv() {
    if (!tenderId) return;
    setExporting(true);
    try {
      const blob = await exportMatchResultsCsv(tenderId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `match-results-${tenderId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : t('matchResults.exportFailed'));
    } finally {
      setExporting(false);
    }
  }

  async function handleReextractAndMatch() {
    if (!tenderId) return;
    try {
      setReextractStep(t('matchResults.reextracting'));
      await reextractTenderMetadata(tenderId);

      setReextractStep(t('matchResults.embedding'));
      await embedTender(tenderId);

      setReextractStep(t('matchResults.matching'));
      await triggerMatching(tenderId);

      setReextractStep(t('matchResults.reextractDone'));
      await load();
    } catch {
      setReextractStep(t('matchResults.reextractFailed'));
    }
  }

  async function handleRerunMatching() {
    if (!tenderId) return;
    try {
      setRerunStep(t('matchResults.embedding'));
      await embedTender(tenderId);

      setRerunStep(t('matchResults.matching'));
      await triggerMatching(tenderId);

      setRerunStep(t('matchResults.reextractDone'));
      await load();
    } catch {
      setRerunStep(t('matchResults.reextractFailed'));
    }
  }

  async function handleSaveSkills(skills: WeightedSkill[]) {
    if (!tenderId) return;
    const updated = await updateTenderSkills(tenderId, skills);
    setWeightedSkills(updated);
  }

  const filteredResults = results.filter((r) => r.score >= minScore);

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
        <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">⟳</span>
        {t('matchResults.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
        {error}
        <button onClick={load} className="ml-4 underline text-red-600 hover:text-red-800">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (!tender || !summary) return null;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
        <ol className="flex items-center gap-2">
          <li><Link to="/tenders" className="hover:text-brand-700">{t('matchResults.breadcrumbTenders')}</Link></li>
          <li aria-hidden="true">/</li>
          <li className="text-gray-800 font-medium truncate max-w-xs">{tender.title}</li>
          <li aria-hidden="true">/</li>
          <li className="text-gray-800 font-medium">{t('matchResults.breadcrumbMatches')}</li>
        </ol>
      </nav>

      {/* Page header */}
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900">{tender.title}</h1>
        <p className="text-sm text-gray-500">{tender.client}</p>
        {(tender.work_location || tender.sector) && (
          <div className="flex flex-wrap gap-3 text-xs text-gray-500 pt-0.5">
            {tender.work_location && (
              <span>{t('matchResults.location')}: <span className="font-medium text-gray-700">{tender.work_location}</span></span>
            )}
            {tender.sector && (
              <span>{t('matchResults.sector')}: <span className="font-medium text-gray-700">{tender.sector}</span></span>
            )}
          </div>
        )}
      </header>

      {/* Action buttons */}
      <section aria-label={t('matchResults.actions')} className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleReextractAndMatch}
          disabled={reextractStep !== null && reextractStep !== t('matchResults.reextractDone') && reextractStep !== t('matchResults.reextractFailed')}
          className="btn-primary text-xs"
          aria-busy={reextractStep !== null && reextractStep !== t('matchResults.reextractDone') && reextractStep !== t('matchResults.reextractFailed')}
        >
          {t('matchResults.reextract')}
        </button>
        <button
          type="button"
          onClick={handleRerunMatching}
          disabled={rerunStep !== null && rerunStep !== t('matchResults.reextractDone') && rerunStep !== t('matchResults.reextractFailed')}
          className="btn-secondary text-xs"
          aria-busy={rerunStep !== null && rerunStep !== t('matchResults.reextractDone') && rerunStep !== t('matchResults.reextractFailed')}
        >
          {t('matchResults.rerunMatching')}
        </button>
        {reextractStep && (
          <span role="status" aria-live="polite" className="text-xs text-gray-600">
            {reextractStep}
          </span>
        )}
        {rerunStep && !reextractStep && (
          <span role="status" aria-live="polite" className="text-xs text-gray-600">
            {rerunStep}
          </span>
        )}
      </section>

      {/* Summary stats */}
      <section
        aria-label={t('matchResults.matchSummaryStats')}
        className="grid grid-cols-2 gap-4 sm:grid-cols-4"
      >
        {[
          { label: t('matchResults.totalCandidates'),  value: summary.total_candidates },
          { label: t('matchResults.topScore'),         value: `${summary.top_score}/100` },
          { label: t('matchResults.averageScore'),     value: `${Math.round(summary.average_score)}/100` },
          { label: t('matchResults.requiredSkills'),   value: tender.required_skills.length },
        ].map(({ label, value }) => (
          <div key={label} className="card text-center">
            <p className="text-sm text-gray-500">{label}</p>
            <p className="mt-1 text-2xl font-bold text-brand-700">{value}</p>
          </div>
        ))}
      </section>

      {/* Weighted skills editor */}
      <SkillEditor
        tenderId={tender.id}
        skills={weightedSkills}
        onSave={handleSaveSkills}
      />

      {/* Nice-to-have skills (not managed by the weighted skill editor) */}
      {tender.nice_to_have_skills.length > 0 && (
        <section className="card space-y-2">
          <h2 className="text-sm font-semibold text-gray-700">{t('matchResults.niceToHaveSkills')}</h2>
          <ul className="flex flex-wrap gap-2">
            {tender.nice_to_have_skills.map((skill) => (
              <SkillBadge key={skill} skill={skill} matched optional />
            ))}
          </ul>
        </section>
      )}

      {/* Detected personas */}
      {personas && personas.personas.length > 0 && (
        <section aria-label={t('matchResults.detectedPersonas', 'Detected Personas')} className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700">{t('matchResults.detectedPersonas', 'Detected Personas')}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {personas.personas.map((persona, i) => (
              <PersonaCard
                key={persona.persona_id ?? `custom-${i}`}
                personaName={persona.persona_name}
                description={persona.description}
                requiredSkills={persona.required_skills}
                reason={persona.reason}
                isCustom={persona.custom}
              />
            ))}
          </div>
        </section>
      )}

      {/* Controls bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Min score filter */}
        <div className="flex items-center gap-2">
          <label htmlFor="min-score" className="text-sm text-gray-600 whitespace-nowrap">
            {t('matchResults.minScore')}
          </label>
          <input
            id="min-score"
            type="range"
            min={0}
            max={100}
            step={5}
            value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))}
            className="w-32 accent-brand-600"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={minScore}
            aria-valuetext={t('matchResults.scoreOutOf', { score: minScore })}
          />
          <span className="text-sm font-medium text-gray-700 w-8">{minScore}</span>
        </div>

        {/* View toggle */}
        <div
          role="group"
          aria-label={t('matchResults.viewMode')}
          className="flex rounded-lg border border-gray-200 overflow-hidden"
        >
          {(['cards', 'table'] as ViewMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              aria-pressed={viewMode === mode}
              className={[
                'px-3 py-1.5 text-xs font-medium capitalize transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                viewMode === mode
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50',
              ].join(' ')}
            >
              {t(`matchResults.${mode}`)}
            </button>
          ))}
        </div>

        <div className="ml-auto flex gap-2">
          <button onClick={load} className="btn-secondary text-xs">
            {t('common.refresh')}
          </button>
          <button
            onClick={handleExportCsv}
            disabled={exporting}
            className="btn-secondary text-xs"
            aria-busy={exporting}
          >
            {exporting ? t('matchResults.exporting') : t('matchResults.exportCsv')}
          </button>
        </div>
      </div>

      {/* Result count */}
      <p className="text-sm text-gray-500" aria-live="polite">
        {t('matchResults.showing', { count: filteredResults.length, total: results.length })}
        {minScore > 0 && t('matchResults.withMinScore', { score: minScore })}
      </p>

      {filteredResults.length === 0 && (
        <div className="card text-center py-10 text-gray-500 text-sm">
          {t('matchResults.noMatchFilter')}
        </div>
      )}

      {/* Cards view */}
      {viewMode === 'cards' && filteredResults.length > 0 && (
        <section aria-label={t('matchResults.candidateCards')}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredResults.map((match) => (
              <MatchCard key={match.id} match={match} tenderId={tender.id} onViewBreakdown={setSelectedMatch} />
            ))}
          </div>
        </section>
      )}

      {/* Table view */}
      {viewMode === 'table' && filteredResults.length > 0 && (
        <section aria-label={t('matchResults.candidateTable')} className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="data-table" aria-label={t('matchResults.matchResultsTable')}>
              <thead>
                <tr>
                  <th scope="col">{t('matchResults.rank')}</th>
                  <th scope="col">{t('matchResults.candidate')}</th>
                  <th scope="col">{t('matchResults.role')}</th>
                  <th scope="col">{t('matchResults.score')}</th>
                  <th scope="col">{t('matchResults.tier')}</th>
                  <th scope="col">{t('matchResults.matchedSkills')}</th>
                  <th scope="col">{t('matchResults.missingSkills')}</th>
                  <th scope="col"><span className="sr-only">{t('common.actions')}</span></th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((match, idx) => (
                  <tr key={match.id}>
                    <td className="font-medium text-gray-500">#{idx + 1}</td>
                    <td className="font-medium text-gray-900">{match.employee_name}</td>
                    <td className="text-gray-600">{match.employee_role}</td>
                    <td>
                      <span
                        className={[
                          'font-bold',
                          match.score >= 80
                            ? 'text-green-600'
                            : match.score >= 60
                            ? 'text-amber-600'
                            : 'text-red-600',
                        ].join(' ')}
                      >
                        {match.score}
                      </span>
                    </td>
                    <td className="capitalize">{match.tier}</td>
                    <td>
                      <span className="text-xs text-gray-600">
                        {match.matched_skills.slice(0, 3).join(', ')}
                        {match.matched_skills.length > 3 && ` +${match.matched_skills.length - 3}`}
                      </span>
                    </td>
                    <td>
                      <span className="text-xs text-red-600">
                        {match.missing_skills.slice(0, 2).join(', ')}
                        {match.missing_skills.length > 2 && ` +${match.missing_skills.length - 2}`}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => setSelectedMatch(match)}
                        className="text-xs text-brand-600 hover:text-brand-800 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 rounded"
                        aria-label={t('matchResults.viewBreakdown', { name: match.employee_name })}
                      >
                        {t('matchResults.breakdown')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Skill breakdown modal/panel */}
      {selectedMatch && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('matchResults.skillBreakdown', { name: selectedMatch.employee_name })}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40 cursor-default"
            onClick={() => setSelectedMatch(null)}
            aria-label={t('common.close')}
          />
          <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selectedMatch.employee_name}</h2>
                <p className="text-sm text-gray-500">{selectedMatch.employee_role}</p>
              </div>
              <button
                onClick={() => setSelectedMatch(null)}
                className="text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                aria-label={t('matchResults.closeBreakdown')}
              >
                ✕
              </button>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('matchResults.tenderRole')}</h3>
              <p className="text-sm text-gray-700 italic border-l-2 border-brand-200 pl-3">
                {selectedMatch.role}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('matchResults.explanation')}</h3>
              <p className="text-sm text-gray-700 italic border-l-2 border-brand-200 pl-3">
                {selectedMatch.explanation}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('matchResults.strengths')}</h3>
              <p className="text-sm text-gray-700 italic border-l-2 border-brand-200 pl-3">
                {selectedMatch.strengths}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('matchResults.fullSkillBreakdown')}</h3>
              <ul className="flex flex-wrap gap-2">
                {selectedMatch.skill_breakdown.map((sb) => (
                  <SkillBadge
                    key={sb.skill}
                    skill={sb.skill}
                    matched={sb.matched}
                    optional={!sb.is_required}
                  />
                ))}
              </ul>
            </div>

            <button onClick={() => setSelectedMatch(null)} className="btn-secondary w-full mt-2">
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
